import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import {
  makeStyles,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  MenuItem
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
}));

const ChatagentsSchema = Yup.object().shape({
  key: Yup.string().required("Requerido"),
  value: Yup.string().required("Requerido")
});

const AgentModal = ({ open, onClose, agentId, id }) => {
  const classes = useStyles();

  const initialState = {
    key: "",
    value: ""
  };

  const [agents, setAgents] = useState(initialState);
  const [availableKeys, setAvailableKeys] = useState([]);

  useEffect(() => {
    const loadAgent = async () => {
      if (!agentId) return;
      try {
        const { data } = await api.get(`/agentsquerry/${agentId}`);
        setAgents(data);
      } catch (err) {
        toastError(err);
      }
    };

    
    const loadAvailableKeys = async () => {
    try {
      const { data } = await api.get("/agentsquerry/available-keys", {
        params: { botId: id }
      });
      if (!agentId && data.length === 0) {
        toast.warning("Ya no hay claves disponibles para configurar.");
        onClose(); // Cierra automáticamente si no hay claves
      } else {
        setAvailableKeys(data);
      }
    } catch (err) {
      toastError(err);
    }
  };

    if (open) {
      loadAgent();
      loadAvailableKeys();
    }

    return () => {
      setAgents(initialState);
      setAvailableKeys([]);
    };
  }, [agentId, open, id]);

  const handleClose = () => {
    onClose();
    setAgents(initialState);
  };

  const handleSaveAgents = async (values) => {
    const updatedValues = {
      ...values,
      botId: id
    };

    try {
      // Validar clave duplicada solo al crear
      if (!agentId) {
        const { data: existing } = await api.get("/agentsquerry", {
          params: { id }
        });

        const claveRepetida = existing.find(
          (item) => item.key === updatedValues.key
        );

        if (claveRepetida) {
          toast.error("Esta clave ya ha sido configurada.");
          return;
        }
      }

      if (agentId) {
        await api.put(`/agentsquerry/${agentId}`, updatedValues);
      } else {
        await api.post("/agentsquerry", updatedValues);
      }

      toast.success(i18n.t("agentModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth scroll="paper">
        <DialogTitle>
          {agentId
            ? i18n.t("agentQueriesModal.title.edit")
            : i18n.t("agentQueriesModal.title.add")}
        </DialogTitle>

        <Formik
          initialValues={agents}
          enableReinitialize
          validationSchema={ChatagentsSchema}
          onSubmit={(values, actions) => {
            handleSaveAgents(values);
            actions.setSubmitting(false);
          }}
        >
          {({ touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <Field
                  as={TextField}
                  select
                  label={i18n.t("agentQueriesModal.form.key")}
                  name="key"
                  fullWidth
                  disabled={!!agentId}
                  error={touched.key && Boolean(errors.key)}
                  helperText={touched.key && errors.key}
                  variant="outlined"
                  margin="dense"
                >
                  {agentId ? (
                    <MenuItem value={agents.key}>{agents.key}</MenuItem>
                  ) : (
                    availableKeys.map((key) => (
                      <MenuItem key={key} value={key}>
                        {key}
                      </MenuItem>
                    ))
                  )}
                </Field>

                <Field
                  as={TextField}
                  label={i18n.t("agentQueriesModal.form.value")}
                  name="value"
                  fullWidth
                  multiline
                  rows={4}
                  error={touched.value && Boolean(errors.value)}
                  helperText={touched.value && errors.value}
                  variant="outlined"
                  margin="dense"
                />
              </DialogContent>

              <DialogActions>
                <Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
                  {i18n.t("agentModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting || (!agentId && availableKeys.length === 0)}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {agentId
                    ? i18n.t("agentModal.buttons.okEdit")
                    : i18n.t("agentModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default AgentModal;
